import { useCallback, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useAdminClaimShiftMutation } from '@/core/services/shift-marketplace'
import { getApiErrorMessage } from '@/utils/errors'

export function useAdminClaimShift(planningId: string, shiftTemplateId: string, startTime: string, endTime: string) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const [isProcessing, setIsProcessing] = useState(false)

  const [adminClaimShiftMutation] = useAdminClaimShiftMutation()

  const adminClaimShift = useCallback(
    async (staffIds: string[], onCompleted?: () => void) => {
      if (!staffIds.length) return

      setIsProcessing(true)

      try {
        // Process each staff assignment sequentially
        const results = []
        const errors = []

        for (const staffId of staffIds) {
          try {
            const result = await adminClaimShiftMutation({
              planningId,
              shiftTemplateId,
              startTime,
              endTime,
              staffId,
            }).unwrap()

            results.push(result)
          } catch (error) {
            console.error(`Failed to claim shift for staff ID: ${staffId}`, error)
            errors.push({ staffId, error })
          }
        }

        const successCount = results.length
        const totalCount = staffIds.length

        if (successCount === totalCount) {
          enqueueSnackbar(
            totalCount > 1 ? t('page.planning.shift.admin.create.multipleSuccess', { count: successCount }) : t('page.planning.shift.admin.create.success'),
            { variant: 'success' }
          )
        } else if (successCount > 0) {
          enqueueSnackbar(
            t('page.planning.shift.admin.create.partialSuccess', {
              success: successCount,
              total: totalCount,
            }),
            { variant: 'warning' }
          )

          // Log errors for debugging
          errors.forEach(({ staffId, error }) => {
            console.error(`Error claiming shift for staff ${staffId}:`, getApiErrorMessage(error))
          })
        } else {
          // All failed
          enqueueSnackbar(t('page.planning.shift.admin.create.allFailed'), { variant: 'error' })
        }
      } catch (error) {
        console.error('Unexpected error in handleAdminClaimShift:', error)
        enqueueSnackbar(getApiErrorMessage(error), { variant: 'error' })
      } finally {
        setIsProcessing(false)
      }
    },
    [planningId, shiftTemplateId, startTime, endTime]
  )

  return [adminClaimShift, isProcessing] as const
}
